import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { EnhancedCard } from "@/components/ui/enhanced-card";
import { UserAvatar } from "@/components/profile/UserAvatar";
import { OfflineIndicator } from "@/components/ui/offline-indicator";
import { NotificationSettingsSection } from "@/components/notifications/NotificationSettingsSection";
import ThemeToggle from "@/components/ThemeToggle";
import {
  FaSignOutAlt,
  FaUser,
  FaKey,
  FaRegCommentDots,
  FaCoffee,
  FaMoon,
  FaSun,
  FaUsers,
  FaCog,
  FaHeart,
  FaShieldAlt,
  FaChevronRight,
} from "react-icons/fa";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { openFeedbackForm } from "@/lib/services/feedbackService";
import { useTheme } from "@/context/ThemeContext";
import { usePageTitle } from "@/hooks/usePageTitle";

const ProfilePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signOut, user, updateProfile } = useAuth();
  const { theme } = useTheme();

  usePageTitle(`${t("nav.profile")} — ${t("app.name")}`);

  const displayName =
    (user?.user_metadata?.full_name as string | undefined)?.trim() ||
    user?.email?.split("@")[0] ||
    "";

  const [fullName, setFullName] = useState(displayName);

  useEffect(() => {
    setFullName(displayName);
  }, [displayName]);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const handleSaveName = async () => {
    const trimmed = fullName.trim();
    if (!trimmed || trimmed === displayName) return;

    setIsSavingName(true);
    try {
      await updateProfile({ full_name: trimmed });
      toast({
        title: t("common.success"),
        description: t("profile.displayNameSaved"),
      });
    } catch (error) {
      toast({
        title: t("errors.error"),
        description: error instanceof Error ? error.message : t("errors.error"),
        variant: "destructive",
      });
    } finally {
      setIsSavingName(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Failed to log out:", error);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: t("errors.validationError"),
        description: t("profile.passwordsDoNotMatch"),
        variant: "destructive",
      });
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast({
        title: t("errors.validationError"),
        description: t("profile.passwordTooShort"),
        variant: "destructive",
      });
      return;
    }

    setIsSubmittingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.newPassword,
      });
      if (error) throw error;

      toast({
        title: t("common.success"),
        description: t("profile.passwordChangeSuccess"),
      });
      setIsChangingPassword(false);
      setPasswordData({ newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast({
        title: t("errors.error"),
        description: error instanceof Error ? error.message : t("profile.passwordChangeError"),
        variant: "destructive",
      });
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-soccer-primary/10 rounded-lg">
          <FaUser className="h-6 w-6 text-soccer-primary" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">{t("nav.profile")}</h1>
          <p className="text-muted-foreground text-sm">{t("profile.settingsDescription")}</p>
        </div>
      </div>

      <EnhancedCard hoverable className="w-full">
        <EnhancedCard.Header>
          <EnhancedCard.Title className="flex items-center gap-2">
            <FaUser className="h-5 w-5 text-soccer-primary" />
            {t("profile.accountInfo")}
          </EnhancedCard.Title>
          <EnhancedCard.Description>{t("profile.accountInfoDescription")}</EnhancedCard.Description>
        </EnhancedCard.Header>
        <EnhancedCard.Content className="space-y-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <UserAvatar
              displayName={displayName}
              avatarUrl={user?.user_metadata?.avatar_url as string | undefined}
              editable
            />
            <div className="flex-1 space-y-4 w-full">
              <div className="space-y-2">
                <Label htmlFor="displayName">{t("profile.name")}</Label>
                <div className="flex gap-2">
                  <Input
                    id="displayName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder={t("profile.name")}
                    className="focus:border-soccer-primary focus:ring-soccer-primary"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleSaveName}
                    disabled={isSavingName || !fullName.trim() || fullName.trim() === displayName}
                  >
                    {isSavingName ? t("common.loading") : t("common.save")}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">{t("profile.displayNameHint")}</p>
              </div>
              <div>
                <Label className="text-sm font-medium">{t("profile.email")}</Label>
                <div className="text-sm text-muted-foreground mt-1">{user?.email}</div>
              </div>
              <div>
                <Label className="text-sm font-medium">{t("profile.memberSince")}</Label>
                <div className="text-sm text-muted-foreground mt-1">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
                </div>
              </div>
            </div>
          </div>
        </EnhancedCard.Content>
      </EnhancedCard>

      <div className="space-y-2">
        <NotificationSettingsSection />
        <p className="text-xs text-muted-foreground px-1">{t("notifications.viewInHeaderHint")}</p>
      </div>

      <EnhancedCard hoverable className="w-full">
        <EnhancedCard.Header>
          <EnhancedCard.Title className="flex items-center gap-2">
            <FaCog className="h-5 w-5 text-soccer-primary" />
            {t("profile.preferences")}
          </EnhancedCard.Title>
          <EnhancedCard.Description>{t("profile.preferencesDescription")}</EnhancedCard.Description>
        </EnhancedCard.Header>
        <EnhancedCard.Content className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === "dark" ? (
                <FaMoon className="h-4 w-4 text-soccer-primary" />
              ) : (
                <FaSun className="h-4 w-4 text-soccer-primary" />
              )}
              <div>
                <div className="font-medium text-sm">{t("profile.theme")}</div>
                <div className="text-xs text-muted-foreground">
                  {theme === "dark" ? t("profile.darkMode") : t("profile.lightMode")}
                </div>
              </div>
            </div>
            <ThemeToggle />
          </div>
          <div className="pt-2 border-t">
            <OfflineIndicator showSyncStatus />
          </div>
        </EnhancedCard.Content>
      </EnhancedCard>

      <EnhancedCard hoverable className="w-full">
        <EnhancedCard.Header>
          <EnhancedCard.Title className="flex items-center gap-2">
            <FaUsers className="h-5 w-5 text-soccer-primary" />
            {t("groups.manageGroups")}
          </EnhancedCard.Title>
          <EnhancedCard.Description>{t("profile.groupSectionDescription")}</EnhancedCard.Description>
        </EnhancedCard.Header>
        <EnhancedCard.Content className="p-0">
          <Link
            to="/group-selection"
            className="flex items-center justify-between px-6 py-3 hover:bg-muted/50 transition-colors"
          >
            <span className="text-sm font-medium">{t("groups.manageGroups")}</span>
            <FaChevronRight className="h-3 w-3 text-muted-foreground" />
          </Link>
        </EnhancedCard.Content>
      </EnhancedCard>

      <EnhancedCard hoverable className="w-full">
        <EnhancedCard.Header>
          <EnhancedCard.Title className="flex items-center gap-2">
            <FaShieldAlt className="h-5 w-5 text-soccer-primary" />
            {t("profile.security")}
          </EnhancedCard.Title>
          <EnhancedCard.Description>{t("profile.securityDescription")}</EnhancedCard.Description>
        </EnhancedCard.Header>
        {!isChangingPassword ? (
          <EnhancedCard.Content>
            <Button
              variant="outline"
              onClick={() => setIsChangingPassword(true)}
              className="hover:bg-soccer-primary/10 hover:border-soccer-primary/30"
            >
              <FaKey className="mr-2 h-4 w-4" />
              {t("profile.changePassword")}
            </Button>
          </EnhancedCard.Content>
        ) : (
          <form onSubmit={handlePasswordChange}>
            <EnhancedCard.Content className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t("profile.newPassword")}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  autoComplete="new-password"
                  className="focus:border-soccer-primary focus:ring-soccer-primary"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("profile.confirmNewPassword")}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  required
                  autoComplete="new-password"
                  className="focus:border-soccer-primary focus:ring-soccer-primary"
                />
              </div>
            </EnhancedCard.Content>
            <EnhancedCard.Footer className="flex flex-row gap-2">
              <Button
                type="submit"
                disabled={isSubmittingPassword}
                className="bg-soccer-primary hover:bg-soccer-primary/90"
              >
                {isSubmittingPassword ? t("common.loading") : t("profile.updatePassword")}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={isSubmittingPassword}
                onClick={() => {
                  setIsChangingPassword(false);
                  setPasswordData({ newPassword: "", confirmPassword: "" });
                }}
              >
                {t("common.cancel")}
              </Button>
            </EnhancedCard.Footer>
          </form>
        )}
      </EnhancedCard>

      <EnhancedCard hoverable className="w-full">
        <EnhancedCard.Header>
          <EnhancedCard.Title className="flex items-center gap-2">
            <FaHeart className="h-5 w-5 text-soccer-primary" />
            {t("profile.support")}
          </EnhancedCard.Title>
          <EnhancedCard.Description>{t("profile.supportDescription")}</EnhancedCard.Description>
        </EnhancedCard.Header>
        <EnhancedCard.Content className="space-y-3">
          <Button variant="outline" className="w-full justify-start" onClick={openFeedbackForm}>
            <FaRegCommentDots className="mr-2 h-4 w-4 text-soccer-primary" />
            {t("profile.giveFeedback")}
          </Button>
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={() => window.open("https://cafecito.app/fulbitostats", "_blank", "noopener")}
          >
            <FaCoffee className="mr-2 h-4 w-4 text-soccer-primary" />
            {t("profile.supportWithCafecito")}
          </Button>
        </EnhancedCard.Content>
      </EnhancedCard>

      <Button variant="destructive" className="w-full" onClick={handleLogout}>
        <FaSignOutAlt className="mr-2 h-4 w-4" />
        {t("auth.signOut")}
      </Button>
    </div>
  );
};

export default ProfilePage;
